import { useState } from "react";
import {
  Box,
  Button,
  Input,
  Grid,
  GridItem,
  Text,
  Flex,
  Tooltip,
} from "@chakra-ui/react";
import { MultiSelect } from "chakra-multiselect";
import { toast, useToast } from "react-toastify";
import { IoChevronBackCircleSharp } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { Formik } from "formik";
import * as Yup from "yup";
import { InputGroup, InputRightElement } from "@chakra-ui/react";
import isAllowed from "../../routing/isAllowed";
import CustomAlertDialog from "../../Components/CustomAlertDialog";
import { createUser } from "../../api";

const UserCreation = () => {
  const [role, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [user, setUser] = useState({});
  const onModalClose = () => setOpen(false);

  const handleGoBack = () => {
    navigate(-1);
  };

  const availableRoles = [
    "CONTRACTOR",
    "CERTIFIER",
    "PLANNER",
    "AUDITOR",
    "L1-TC-ISSUER",
    "L2-TC-ISSUER",
    "ADMIN",
  ];

  const handleCreateUser = async () => {
    try {
      const finalUserObject = {
        ...user,
        role,
      };
      const result = await createUser(finalUserObject);
      if (result?.status === 200) {
        toast.success("User created successfully");
        setRoles([]);
      } else {
        toast.error(result?.response?.data?.message);
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Box marginBottom={"1rem"} p="1rem">
      <Box
        display={"flex"}
        alignItems="center"
        cursor={"pointer"}
        onClick={handleGoBack}
      >
        <IoChevronBackCircleSharp color={"#52BBFF"} size={30} />
        &nbsp;
        <Text
          color={"#52BBFF"}
          fontSize={{ base: "sm", md: "sm", lg: "md" }}
          fontWeight={"800"}
          fontFamily={"font.roboto"}
        >
          Go Back
        </Text>
      </Box>
      <Box
        mt="3rem"
        bg="white"
        p="2rem"
        borderRadius="15px"
        boxShadow={"rgba(100, 100, 111, 0.2) 0px 10px 26px 0px"}
      >
        <Flex justifyContent={"space-between"} alignItems="center">
          <Box marginTop="2rem">
            <Text
              color={"#141D43"}
              fontSize="2xl"
              fontWeight={"800"}
              fontFamily={"font.roboto"}
            >
              <Box
                as="span"
                borderRight={"5px solid #2272D5"}
                height="40px"
                marginRight={"1rem"}
              ></Box>
              Create User
            </Text>
          </Box>
        </Flex>
        <Formik
          initialValues={{
            name: "",
            email: "",
            phone: "",
            password: "",
          }}
          onSubmit={async (values, { resetForm }) => {
            if (role?.length < 1) {
              toast.error("Please select one role to create the user.");
              return;
            }
            setUser(values);
            setOpen(true);
          }}
          validationSchema={Yup.object().shape({
            name: Yup.string().required("User's name is required"),
            phone: Yup.string()
              .required("User's phone is required")
              .min(10, "User's phone must be at least 10 characters")
              .max(10, "User's phone must be 10 characters"),
            email: Yup.string()
              .required("Email is required")
              .matches(
                new RegExp(
                  /^[a-zA-Z][a-zA-Z0-9._-]*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
                ),
                "Invalid email address"
              ),
            password: Yup.string()
              .required("Password is required")
              .matches(
                new RegExp(
                  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/
                ),
                "Password must be at least 8 characters and include at least one uppercase letter, one lowercase letter, one number, and one special character"
              ),
          })}
        >
          {(props) => {
            const {
              values,
              touched,
              errors,
              handleChange,
              handleBlur,
              handleSubmit,
              setFieldValue,
            } = props;
            return (
              <Box paddingX="2rem" mb="1rem">
                <Box mt="2rem">
                  <Grid
                    templateColumns="repeat(2, 1fr)"
                    marginTop={"1rem"}
                    gap={"1rem"}
                  >
                    <GridItem>
                      <Text
                        color={"#141D43"}
                        fontSize="md"
                        fontWeight={"700"}
                        fontFamily={"font.roboto"}
                      >
                        Name &nbsp;
                        <Text as="span" color="red">
                          *
                        </Text>
                      </Text>
                      <Input
                        type="text"
                        color={"#141D43"}
                        marginTop="10px"
                        bg={"#fff"}
                        border="2px solid  #8fd0fd !important"
                        w="60%"
                        opacity={"1 !important"}
                        id="name"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        _focusVisible={
                          errors.name && touched.name
                            ? {
                                border: "1px solid red !important",
                              }
                            : {
                                border: "1px solid #6C3B1C",
                              }
                        }
                        placeholder="Enter name"
                        _placeholder={{
                          opacity: 1,
                          color: "",
                          fontSize: "14px",
                        }}
                      />
                      {errors.name && touched.name && (
                        <Box color="red.500" marginTop={"10px"}>
                          {errors.name}
                        </Box>
                      )}
                    </GridItem>
                    <GridItem>
                      <Text
                        color={"#141D43"}
                        fontSize="md"
                        fontWeight={"700"}
                        fontFamily={"font.roboto"}
                      >
                        Phone &nbsp;
                        <Text as="span" color="red">
                          *
                        </Text>
                      </Text>
                      <Input
                        color={"#141D43"}
                        marginTop="10px"
                        bg={"#fff"}
                        border="2px solid  #8fd0fd !important"
                        w="60%"
                        id="phone"
                        opacity={"1 !important"}
                        type="number"
                        value={values.phone}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        _focusVisible={
                          errors.phone && touched.phone
                            ? {
                                border: "1px solid red !important",
                              }
                            : {
                                border: "1px solid #6C3B1C",
                              }
                        }
                        placeholder="Enter phone"
                        _placeholder={{
                          opacity: 1,
                          color: "",
                          fontSize: "14px",
                        }}
                      />
                      {errors.phone && touched.phone && (
                        <Box color="red.500" marginTop={"10px"}>
                          {errors.phone}
                        </Box>
                      )}
                    </GridItem>
                  </Grid>
                </Box>
                <Box mt="2rem">
                  <Grid
                    templateColumns="repeat(2, 1fr)"
                    marginTop={"1rem"}
                    gap="1rem"
                  >
                    <GridItem>
                      <Text
                        color={"#141D43"}
                        fontSize="md"
                        fontWeight={"700"}
                        fontFamily={"font.roboto"}
                      >
                        Email &nbsp;
                        <Text as="span" color="red">
                          *
                        </Text>
                      </Text>
                      <Input
                        color={"#141D43"}
                        marginTop="10px"
                        bg={"#fff"}
                        border="2px solid  #8fd0fd !important"
                        w="60%"
                        opacity={"1 !important"}
                        id="email"
                        type="email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        _focusVisible={
                          errors.email && touched.email
                            ? {
                                border: "1px solid red !important",
                              }
                            : {
                                border: "1px solid #6C3B1C",
                              }
                        }
                        placeholder="Enter email"
                        _placeholder={{
                          opacity: 1,
                          color: "",
                          fontSize: "14px",
                        }}
                      />
                      {errors.email && touched.email && (
                        <Box color="red.500" marginTop={"10px"}>
                          {errors.email}
                        </Box>
                      )}
                    </GridItem>
                    <GridItem>
                      <Text
                        color={"#141D43"}
                        fontSize="md"
                        fontWeight={"700"}
                        fontFamily={"font.roboto"}
                      >
                        Password &nbsp;
                        <Text as="span" color="red">
                          *
                        </Text>
                      </Text>
                      <Tooltip label="Please make sure to include one small, one capital, one number and one special character in password">
                        <InputGroup marginTop="10px" width="60%">
                          <Input
                            color={"#141D43"}
                            bg={"#fff"}
                            border="2px solid #8fd0fd !important"
                            type={showPassword ? "text" : "password"}
                            id="password"
                            value={values.password}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            _focusVisible={
                              errors.password && touched.password
                                ? {
                                    border: "1px solid red !important",
                                  }
                                : {
                                    border: "1px solid #6C3B1C",
                                  }
                            }
                            placeholder="Enter email"
                            _placeholder={{
                              opacity: 1,
                              color: "",
                              fontSize: "14px",
                            }}
                          />
                          <InputRightElement width="4.5rem">
                            <Box
                              h="1.75rem"
                              size="sm"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? (
                                <AiOutlineEyeInvisible
                                  color="#52BBFF"
                                  size={24}
                                  marginBottom={"10px"}
                                  onClick={() => setShowPassword(!showPassword)}
                                />
                              ) : (
                                <AiOutlineEye
                                  color="#52BBFF"
                                  size={24}
                                  marginBottom={"10px"}
                                  onClick={() => setShowPassword(!showPassword)}
                                />
                              )}
                            </Box>
                          </InputRightElement>
                        </InputGroup>
                      </Tooltip>
                      {errors.password && touched.password && (
                        <Box color="red.500" marginTop={"10px"}>
                          {errors.password}
                        </Box>
                      )}
                    </GridItem>
                  </Grid>
                </Box>
                <Box mt="1rem">
                  <Grid templateColumns="repeat(2, 1fr)" marginTop={"1rem"}>
                    <GridItem colSpan={1}>
                      <Text
                        color={"#141D43"}
                        fontSize="md"
                        fontWeight={"700"}
                        fontFamily={"font.roboto"}
                      >
                        Select Role(s) &nbsp;
                        <Text as="span" color="red">
                          *
                        </Text>
                      </Text>
                      <MultiSelect
                        marginTop={"10px"}
                        options={availableRoles}
                        toggleAllCheckboxVisibility="always"
                        bgColor="white"
                        color={" #464C64"}
                        w="10%"
                        border="2px solid  #8fd0fd !important"
                        id="role"
                        value={role}
                        onChange={(value) => {
                          setRoles(value);
                        }}
                      />
                    </GridItem>
                  </Grid>
                  {errors.role && touched.role && (
                    <Box color="red.500" marginTop={"10px"}>
                      {errors.role}
                    </Box>
                  )}
                </Box>
                <Box mt="2rem">
                  <Button
                    bg="brand.secondaryColor"
                    color="brand.white"
                    colorScheme="brand.secondaryColor"
                    w="193px"
                    h="50px"
                    onClick={handleSubmit}
                    mt="10px"
                  >
                    Approve
                  </Button>
                </Box>
              </Box>
            );
          }}
        </Formik>
        <Box>
          <CustomAlertDialog
            isOpen={open}
            onClose={onModalClose}
            title="User Creation"
            content="Are you sure? You want to create this user."
            cancelButtonText="Cancel"
            confirmButtonText="Create"
            cancelButtonColor="gray"
            confirmButtonColor="#52BBFF"
            onConfirm={handleCreateUser}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default isAllowed(UserCreation);
